const VIEWPORT_EPSILON_PX = 1;
const MIN_VISIBLE_RECT_PX = 1;

type RectLike = {
  left: number;
  top: number;
  right: number;
  bottom: number;
  width?: number;
  height?: number;
};

export type VisibleIframeRect = {
  left: number;
  top: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
};

export type VisibleIframeProbePoint = {
  x: number;
  y: number;
};

type VisibleIframeRectInput = {
  iframeRect: RectLike;
  containerRect: RectLike;
  viewportWidth?: number | null;
  viewportHeight?: number | null;
};

const finiteNumber = (value?: number | null): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

const rectWidth = (rect: RectLike): number => {
  const explicit = finiteNumber(rect.width);
  return explicit !== null ? explicit : rect.right - rect.left;
};

const rectHeight = (rect: RectLike): number => {
  const explicit = finiteNumber(rect.height);
  return explicit !== null ? explicit : rect.bottom - rect.top;
};

export function resolveVisibleIframeRect({
  iframeRect,
  containerRect,
  viewportWidth,
  viewportHeight,
}: VisibleIframeRectInput): VisibleIframeRect | null {
  const viewportRight =
    finiteNumber(viewportWidth) ?? Math.max(containerRect.right, iframeRect.right);
  const viewportBottom =
    finiteNumber(viewportHeight) ?? Math.max(containerRect.bottom, iframeRect.bottom);

  const visibleLeft = Math.max(iframeRect.left, containerRect.left, 0);
  const visibleTop = Math.max(iframeRect.top, containerRect.top, 0);
  const visibleRight = Math.min(
    iframeRect.left + rectWidth(iframeRect),
    containerRect.right,
    viewportRight,
  );
  const visibleBottom = Math.min(
    iframeRect.top + rectHeight(iframeRect),
    containerRect.bottom,
    viewportBottom,
  );

  const width = visibleRight - visibleLeft;
  const height = visibleBottom - visibleTop;
  if (width <= MIN_VISIBLE_RECT_PX || height <= MIN_VISIBLE_RECT_PX) {
    return null;
  }

  return {
    left: visibleLeft - iframeRect.left,
    top: visibleTop - iframeRect.top,
    right: visibleRight - iframeRect.left,
    bottom: visibleBottom - iframeRect.top,
    width,
    height,
  };
}

export function resolveVisibleIframeProbePoints(
  input: VisibleIframeRectInput,
): VisibleIframeProbePoint[] {
  const visibleRect = resolveVisibleIframeRect(input);
  if (!visibleRect) {
    return [];
  }

  const fractions: Array<[number, number]> = [
    [0.5, 0.5],
    [0.5, 0.35],
    [0.5, 0.65],
    [0.5, 0.2],
    [0.5, 0.8],
    [0.4, 0.5],
    [0.6, 0.5],
  ];

  const seen = new Set<string>();
  return fractions
    .map(([xFrac, yFrac]) => ({
      x: visibleRect.left + visibleRect.width * xFrac,
      y: visibleRect.top + visibleRect.height * yFrac,
    }))
    .filter((point) => {
      const key = `${Math.round(point.x)}:${Math.round(point.y)}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
}

const MEDIA_SELECTOR =
  "img,svg,video,canvas,iframe,object,embed,math,table";

const rectsOverlap = (a: RectLike, b: RectLike): boolean => {
  const overlapWidth = Math.min(a.right, b.right) - Math.max(a.left, b.left);
  const overlapHeight = Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top);
  return overlapWidth > MIN_VISIBLE_RECT_PX && overlapHeight > MIN_VISIBLE_RECT_PX;
};

const rectHasArea = (rect: RectLike): boolean =>
  rectWidth(rect) > MIN_VISIBLE_RECT_PX && rectHeight(rect) > MIN_VISIBLE_RECT_PX;

const isIgnoredElement = (
  element: Element | null,
  ignoreClass = "epubjs-ignore",
): boolean => {
  if (!element) {
    return true;
  }
  const closestIgnored = ignoreClass
    ? element.closest?.(`.${ignoreClass}`) || null
    : null;
  if (closestIgnored && closestIgnored.id !== "epub-content-wrapper") {
    return true;
  }
  return element.id === "epub-content-wrapper";
};

const elementHasRenderablePayload = (
  element: Element,
  ignoreClass = "epubjs-ignore",
): boolean => {
  if (isIgnoredElement(element, ignoreClass)) {
    return false;
  }

  const tagName = element.tagName.toLowerCase();
  const isGenericWrapper = tagName === "div" || tagName === "span";
  const hasMedia =
    element.matches(MEDIA_SELECTOR) || !!element.querySelector(MEDIA_SELECTOR);
  const hasDirectText = Array.from(element.childNodes).some(
    (node) =>
      node.nodeType === Node.TEXT_NODE && (node.textContent?.trim().length || 0) > 0,
  );
  if (isGenericWrapper && element.childElementCount > 0 && !hasMedia && !hasDirectText) {
    return false;
  }

  return (element.textContent?.trim().length || 0) > 0 || hasMedia;
};

const getElementRects = (element: Element): DOMRect[] => {
  const rectList = Array.from(element.getClientRects?.() || []);
  return rectList.length > 0 ? rectList : [element.getBoundingClientRect()];
};

const getTextNodeRects = (node: Text): DOMRect[] => {
  const doc = node.ownerDocument;
  if (!doc?.createRange) {
    return [];
  }

  const range = doc.createRange();
  try {
    range.selectNodeContents(node);
    return Array.from(range.getClientRects?.() || []).filter(rectHasArea);
  } finally {
    range.detach?.();
  }
};

const collectRenderableContentRects = (
  doc: Document,
  ignoreClass = "epubjs-ignore",
): DOMRect[] => {
  const body = doc.body;
  if (!body) {
    return [];
  }

  const rects: DOMRect[] = [];
  const showText = doc.defaultView?.NodeFilter?.SHOW_TEXT ?? 4;
  const walker = doc.createTreeWalker(body, showText);
  let textNode = walker.nextNode() as Text | null;
  while (textNode) {
    const text = textNode.textContent?.trim() || "";
    const parent = textNode.parentElement;
    if (text && !isIgnoredElement(parent, ignoreClass)) {
      const textRects = getTextNodeRects(textNode);
      if (textRects.length > 0) {
        rects.push(...textRects);
      } else if (parent && elementHasRenderablePayload(parent, ignoreClass)) {
        rects.push(...getElementRects(parent).filter(rectHasArea));
      }
    }
    textNode = walker.nextNode() as Text | null;
  }

  for (const element of Array.from(body.querySelectorAll(MEDIA_SELECTOR))) {
    if (isIgnoredElement(element, ignoreClass)) {
      continue;
    }
    rects.push(...getElementRects(element).filter(rectHasArea));
  }

  return rects;
};

export function resolvePaginatedContentExtent({
  scrollExtent,
  renderedExtent,
  pageExtent,
}: {
  scrollExtent: number;
  renderedExtent: number;
  pageExtent: number;
}): number {
  if (renderedExtent <= 0) {
    return scrollExtent;
  }
  if (scrollExtent <= 0 || renderedExtent >= scrollExtent) {
    return renderedExtent;
  }

  const trustworthyTrim =
    pageExtent > 0 ? pageExtent + VIEWPORT_EPSILON_PX : VIEWPORT_EPSILON_PX;
  return scrollExtent - renderedExtent <= trustworthyTrim
    ? renderedExtent
    : scrollExtent;
}

export function hasContentRectInVisibleIframeRect(
  doc: Document,
  visibleRect: VisibleIframeRect,
  ignoreClass = "epubjs-ignore",
): boolean {
  return collectRenderableContentRects(doc, ignoreClass).some((rect) =>
    rectsOverlap(rect, visibleRect),
  );
}

export function measureRenderableContentExtent(
  doc: Document,
  axis: "horizontal" | "vertical" = "horizontal",
  ignoreClass = "epubjs-ignore",
): number {
  let minExtent = 0;
  let maxExtent = 0;
  for (const rect of collectRenderableContentRects(doc, ignoreClass)) {
    const start = axis === "horizontal" ? rect.left : rect.top;
    const end = axis === "horizontal" ? rect.right : rect.bottom;
    minExtent = Math.min(minExtent, start);
    maxExtent = Math.max(maxExtent, end);
  }

  return maxExtent - minExtent;
}

export type PaginatedViewportMeasurement = {
  width: number;
  height: number;
  isFullscreen: boolean;
};

export type StabilizedPaginatedViewport = PaginatedViewportMeasurement & {
  rawHeight: number;
  policy: "raw" | "promote-max-height" | "preserve-max-height" | "reset";
};

export function stabilizePaginatedViewport(
  next: PaginatedViewportMeasurement,
  previous: StabilizedPaginatedViewport | null,
): StabilizedPaginatedViewport {
  const baseViewport: StabilizedPaginatedViewport = {
    ...next,
    rawHeight: next.height,
    policy: "raw",
  };

  if (!previous) {
    return baseViewport;
  }

  const widthChanged =
    Math.abs(next.width - previous.width) > VIEWPORT_EPSILON_PX;
  const fullscreenChanged = next.isFullscreen !== previous.isFullscreen;

  if (widthChanged || fullscreenChanged || next.isFullscreen) {
    return {
      ...baseViewport,
      policy: "reset",
    };
  }

  if (next.height > previous.height + VIEWPORT_EPSILON_PX) {
    return {
      ...baseViewport,
      policy: "promote-max-height",
    };
  }

  if (next.height + VIEWPORT_EPSILON_PX < previous.height) {
    return {
      ...baseViewport,
      height: previous.height,
      policy: "preserve-max-height",
    };
  }

  return baseViewport;
}
